import React from 'react';
import GuidelineSection from '../components/GuidelineSection';
import SubSection from '../components/SubSection';
import Table from '../components/Table';
import TableOfContents from '../components/TableOfContents';
const ClinicalTrials: React.FC = () => {
  const tocItems = [{
    id: 'introduction',
    title: 'Introduction',
    level: 1
  }, {
    id: 'overview',
    title: 'Overview of Clinical Trials',
    level: 1
  }, {
    id: 'featured-trials',
    title: 'Featured Clinical Trials',
    level: 1
  }, {
    id: 'find-trials',
    title: 'How to Find Relevant Trials',
    level: 1
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-143">
      <div className="lg:col-span-3" data-id="element-144">
        <GuidelineSection id="clinical-trials" title="2. CLINICAL TRIALS" data-id="element-145">
          <SubSection id="introduction" title="2.1 Introduction" data-id="element-146">
            <p className="mb-4" data-id="element-147">
              Clinical trials are research studies that test new treatments or
              new ways of using existing treatments. For late-stage NSCLC,
              clinical trials may offer access to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-148">
              <li data-id="element-149">
                Novel targeted therapies for emerging biomarkers (KRAS G12D,
                NRG1, HER2, etc.)
              </li>
              <li data-id="element-150">
                Next-generation immunotherapies (bispecific antibodies, cancer
                vaccines)
              </li>
              <li data-id="element-151">Combination approaches not yet FDA-approved</li>
              <li data-id="element-152">Drug sensitivity-guided treatment selection</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-153">
              Eligibility Criteria typically include:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-154">
              <li data-id="element-155">
                Confirmed NSCLC diagnosis with specific stage requirements
              </li>
              <li data-id="element-156">Specific biomarker status (mutation-positive or negative)</li>
              <li data-id="element-157">
                Prior treatment history (treatment-naive vs. previously treated)
              </li>
              <li data-id="element-158">Performance status (ECOG 0-1 or 0-2)</li>
              <li data-id="element-159">Adequate organ function</li>
            </ul>
            <p className="mb-4" data-id="element-160">
              <strong data-id="element-161">Geographic Considerations:</strong> Most trials are
              available at major academic medical centers. Travel may be
              required for participation.
            </p>
          </SubSection>
          <SubSection id="overview" title="2.2 Overview of Clinical Trials in NSCLC" data-id="element-162">
            <p className="mb-4" data-id="element-163">
              As of October 2025, there are over 800 active clinical trials for
              patients with NSCLC across all stages in the United States. For
              late-stage (Stage IV) NSCLC, key areas of investigation include:
            </p>
            <p className="font-medium mb-2" data-id="element-164">Major Categories:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-165">
              <li data-id="element-166">
                KRAS-targeted therapies: Beyond G12C to G12D, G12V (zoldonrasib,
                RMC-6236)
              </li>
              <li data-id="element-167">
                EGFR innovations: Fourth-generation TKIs, ADCs, bispecific
                antibodies
              </li>
              <li data-id="element-168">
                Novel immune checkpoint inhibitors: TIGIT, LAG-3, TIM-3
                combinations
              </li>
              <li data-id="element-169">
                Cancer vaccines: Personalized mRNA vaccines (similar to melanoma
                approaches)
              </li>
              <li data-id="element-170">Cellular therapies: TIL therapy, CAR-T, NK cell therapy</li>
              <li data-id="element-171">ADC development: Novel targets beyond HER2 and TROP2</li>
            </ul>
          </SubSection>
          <SubSection id="featured-trials" title="2.3 Featured Clinical Trials for Late-Stage NSCLC" data-id="element-172">
            <p className="mb-4" data-id="element-173">Table of Selected Trials (Examples)</p>
            <Table headers={['Trial Name', 'Phase', 'Target/Intervention', 'Eligibility', 'Status/Notes']} rows={[['QUILT 2.023[50]', 'III', 'Anktiva (N-803) + pembrolizumab + chemo vs SOC', 'Stage 3-4 NSCLC, no driver mutations', 'Actively recruiting (Cohort D only: N-803 with nivolumab-ipilimumab). No published results as of October 2025.'], ['CodeBreak 201[51]', 'II', 'Sotorasib vs docetaxel', 'KRAS G12C mutation, previously treated', 'Actively recruiting. Dose-comparison trial (960 mg vs 240 mg daily) as FDA post-marketing requirement.'], ['MARIPOSA-2[52]', 'III', 'Amivantamab + chemotherapy', 'EGFR-mutated, post-osimertinib', 'COMPLETED with published results. Primary results published October 2023 showed median PFS of 6.3 months for amivantamab-chemotherapy versus 4.2 months for chemotherapy alone (P<0.001). FDA approved amivantamab plus chemotherapy for post-osimertinib EGFR-mutant NSCLC.'], ['Personalized Cancer Vaccine Trial[53]', 'II', 'mRNA vaccine + pembrolizumab', 'Post-surgical, high-risk recurrence', 'Active enrollment at MSK and collaborating sites. Two trials test therapeutic mRNA-based cancer vaccines after surgery to prevent NSCLC recurrence.'], ['DESTINY-Lung04[54]', 'III', 'T-DXd (trastuzumab deruxtecan)', 'HER2-mutated NSCLC', 'Actively recruiting for Phase 3 randomized comparison versus platinum-pemetrexed-pembrolizumab as first-line treatment. Primary completion estimated June 2025.']]} data-id="element-174" />
            <div className="bg-yellow-50 p-4 rounded-lg mt-6 mb-6" data-id="element-175">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-176">
                Important Note on CITYSCAPE/SKYSCRAPER Trials:
              </h4>
              <p data-id="element-177">
                The SKYSCRAPER-01 Phase 3 trial failed to confirm CITYSCAPE's
                promising tiragolumab data, representing an important setback
                for TIGIT inhibitors in NSCLC.[55]
              </p>
            </div>
          </SubSection>
          <SubSection id="find-trials" title="2.4 How to Find Relevant Clinical Trials" data-id="element-178">
            <p className="mb-4" data-id="element-179">Visit ClinicalTrials.gov and search using:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-180">
              <li data-id="element-181">
                Your specific biomarker (e.g., 'EGFR exon 20 insertion NSCLC')
              </li>
              <li data-id="element-182">
                Your treatment history ('previously treated' or
                'treatment-naive')
              </li>
              <li data-id="element-183">Your location</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-184">Additional Resources:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-185">
              <li data-id="element-186">Cancer Commons Trial Finder (cancer-commons.org)</li>
              <li data-id="element-187">LUNGevity HealthTree (lungevity.org/clinical-trials)</li>
              <li data-id="element-188">
                Ask your oncologist for trial recommendations based on your
                profile
              </li>
            </ul>
          </SubSection>
        </GuidelineSection>
      </div>
      <div className="lg:col-span-1" data-id="element-189">
        <div className="lg:sticky lg:top-20" data-id="element-190">
          <TableOfContents items={tocItems} data-id="element-191" />
        </div>
      </div>
    </div>;
};
export default ClinicalTrials;